import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  ServiceItem,
  gbpServicesApi,
} from "../../../services/gbp";
import { Profile } from "../types";
import { buildServiceKeywordSuggestions } from "../profile-content-suggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import { readGabaritoState, splitKeywords } from "./gabarito-utils";

// ==================== Helpers ====================

const formatCategoryLabel = (raw?: string): string => {
  if (!raw) return "";
  let cleaned = raw.replace(/^categories\//, "").replace(/^gcid:/, "");
  cleaned = cleaned.replace(/[_-]/g, " ");
  return cleaned
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ==================== Animations ====================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const slideDown = keyframes`
  from { opacity: 0; max-height: 0; }
  to   { opacity: 1; max-height: 800px; }
`;

// ==================== Styled Components ====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .material-icons {
    font-size: 22px;
    color: #4285f4;
  }
`;

const ServiceCount = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 4px;
`;

const AddServiceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #4285f4;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  .material-icons {
    font-size: 18px;
  }

  &:hover {
    background: #1a73e8;
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.3);
  }

  &:active {
    transform: scale(0.97);
  }
`;

/* ---------- Filtro por categoria ---------- */

const FilterBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active?: boolean }>`
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? "#4285f4" : "#e5e7eb")};
  background: ${({ $active }) => ($active ? "#eff6ff" : "#fff")};
  color: ${({ $active }) => ($active ? "#1d4ed8" : "#6b7280")};
  transition: all 0.15s ease;

  &:hover {
    border-color: #4285f4;
    color: #1d4ed8;
  }
`;

/* ---------- Lista de serviços ---------- */

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 3px solid #22c55e;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    border-color: #d1d5db;
  }
`;

const ServiceIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #dcfce7;

  .material-icons {
    font-size: 18px;
    color: #16a34a;
  }
`;

const ServiceBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ServiceLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
  margin-bottom: 2px;
  word-break: break-word;
`;

const ServiceMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const CategoryBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6366f1;
  background: #eef2ff;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #0891b2;
  background: #ecfeff;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
`;

const ServiceDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 6px 0 0;
  line-height: 1.45;
  word-break: break-word;
`;

/* ---------- Action Buttons ---------- */

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
`;

const IconBtn = styled.button<{ $color?: string; $hoverBg?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  color: ${({ $color }) => $color || "#6b7280"};

  .material-icons {
    font-size: 16px;
  }

  &:hover {
    background: ${({ $hoverBg }) => $hoverBg || "#f3f4f6"};
    border-color: ${({ $color }) => $color || "#d1d5db"};
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

/* ---------- Confirm Delete ---------- */

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeIn} 0.15s ease;
`;

const ConfirmBox = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: ${fadeIn} 0.2s ease;
`;

const ConfirmTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  .material-icons {
    font-size: 22px;
    color: #ef4444;
  }
`;

const ConfirmText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ConfirmServiceName = styled.strong`
  color: #1f2937;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

/* ---------- Formulário ---------- */

const FormOverlay = styled.div`
  animation: ${slideDown} 0.3s ease;
  overflow: hidden;
`;

const ServiceForm = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
`;

const FormTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .material-icons {
    font-size: 20px;
    color: #4285f4;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const TextArea = styled.textarea`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  min-height: 72px;
  resize: vertical;
  background: white;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 3px rgba(66, 133, 244, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const Btn = styled.button<{ $variant?: "primary" | "ghost" | "danger" }>`
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;

  .material-icons {
    font-size: 17px;
  }

  ${({ $variant }) =>
    $variant === "primary"
      ? css`
          background: #4285f4;
          color: white;
          border: none;
          &:hover {
            background: #1a73e8;
          }
          &:disabled {
            background: #93c5fd;
            cursor: not-allowed;
          }
        `
      : $variant === "danger"
        ? css`
            background: #ef4444;
            color: white;
            border: none;
            &:hover {
              background: #dc2626;
            }
            &:disabled {
              background: #fca5a5;
              cursor: not-allowed;
            }
          `
        : css`
            background: transparent;
            color: #64748b;
            border: 1px solid #e2e8f0;
            &:hover {
              background: #f1f5f9;
              color: #334155;
            }
          `}
`;

/* ---------- Estados vazios / Loading / Feedback ---------- */

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: #9ca3af;

  .material-icons {
    font-size: 44px;
    color: #d1d5db;
    margin-bottom: 12px;
  }

  h4 {
    font-size: 15px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 6px;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #6b7280;

  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e5e7eb;
    border-top-color: #4285f4;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
  }

  p {
    margin-top: 14px;
    font-size: 13px;
  }
`;

const Toast = styled.div<{ $type: "error" | "success" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  animation: ${fadeIn} 0.25s ease;

  ${({ $type }) =>
    $type === "error"
      ? css`
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
        `
      : css`
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;
        `}

  .material-icons {
    font-size: 18px;
  }
`;

const SuggestionPanel = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const SuggestionTitle = styled.div`
  color: #0c4a6e;
  font-size: 14px;
  font-weight: 700;
`;

const SuggestionGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const SuggestionButton = styled.button`
  background: #ffffff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  color: #1e40af;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  padding: 10px 12px;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: #eff6ff;
    border-color: #4285f4;
  }
`;

// ==================== Component ====================

interface ServicesTabProps {
  profile: Profile | null;
  userId?: number;
}

export const ServicesTab: React.FC<ServicesTabProps> = ({ profile }) => {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");

  // Form state
  const [formMode, setFormMode] = useState<"closed" | "add" | "edit">("closed");
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [formData, setFormData] = useState<ServiceItem>({
    offered: true,
    label: "",
    description: "",
    categoryId: "",
  });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{
    index: number;
    label: string;
  } | null>(null);

  // ---------- Derivados ----------

  const uniqueCategories = useMemo(() => {
    const set = new Set<string>();
    services.forEach((s) => {
      const cat = s.categoryId || s.serviceTypeId;
      if (cat) set.add(cat);
    });
    return Array.from(set);
  }, [services]);

  const filteredServices = useMemo(() => {
    if (activeFilter === "all") return services;
    return services.filter(
      (s) => (s.categoryId || s.serviceTypeId) === activeFilter
    );
  }, [services, activeFilter]);

  const suggestedServices = useMemo(
    () => {
      if (!profile) return [];
      const g = profile.id ? readGabaritoState(profile.id) : null;
      const kws = g
        ? [
            ...splitKeywords(g.postKeywords || ""),
            ...splitKeywords(g.commentKeywords || ""),
          ]
        : [];
      return buildServiceKeywordSuggestions(profile, kws);
    },
    [profile],
  );

  // publicId do perfil (UUID)
  const publicId = profile?.id;

  // ---------- Fetch Services ----------

  const fetchServices = useCallback(async () => {
    if (!publicId) return;

    try {
      setLoading(true);
      setError(null);

      const [servicesData, categoriesData] = await Promise.all([
        gbpServicesApi.listServices(publicId),
        gbpServicesApi.listServiceCategories(publicId),
      ]);

      // Garante que sempre sejam arrays
      const servicesList = Array.isArray(servicesData) ? servicesData : [];
      const categoriesList = Array.isArray(categoriesData) ? categoriesData : [];

      setServices(servicesList);
      setCategories(categoriesList);
    } catch (err) {
      console.error("[ServicesTab.fetchServices]", err);
      // Não interrompe a renderização apenas mostra vazio
      setServices([]);
      setCategories([]);
      // Mostra mensagem apenas se houver erro real
      const message = err instanceof Error ? err.message : String(err);
      if (!message.includes("all attempts failed")) {
        setError("Não foi possível carregar os serviços.");
      }
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // ---------- Form Helpers ----------

  const resetForm = () => {
    setFormData({ offered: true, label: "", description: "", categoryId: "" });
    setFormMode("closed");
    setEditingIndex(-1);
  };

  const openAddForm = () => {
    setFormData({ offered: true, label: "", description: "", categoryId: "" });
    setFormMode("add");
    setEditingIndex(-1);
  };

  const openSuggestedService = (service: ServiceItem) => {
    setFormData(service);
    setFormMode("add");
    setEditingIndex(-1);
  };

  const openEditForm = (index: number) => {
    const service = services[index];
    setFormData({ ...service });
    setFormMode("edit");
    setEditingIndex(index);
  };

  // ---------- Stable getters ----------

  // ---------- CRUD Handlers ----------

  /** Adicionar novo serviço via POST */
  const handleAddService = async () => {
    if (!publicId || !formData.label) return;

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.createService(publicId, formData);
      setServices(result);
      resetForm();
      setSuccess("Serviço adicionado com sucesso!");
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError("Erro ao adicionar serviço.");
      setTimeout(() => setError(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  /** Editar serviço existente via PUT (envia lista completa atualizada) */
  const handleEditService = async () => {
    if (!publicId || editingIndex < 0 || !formData.label) return;

    const updatedList = services.map((s, i) =>
      i === editingIndex ? { ...formData, offered: true } : s
    );

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.updateServices(publicId, updatedList);
      setServices(result);
      resetForm();
      setSuccess("Serviço atualizado com sucesso!");
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError("Erro ao atualizar serviço.");
      setTimeout(() => setError(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  /** Excluir serviço via PUT (envia lista sem o item removido) */
  const handleDeleteService = async (index: number) => {
    if (!publicId) return;

    const updatedList = services.filter((_, i) => i !== index);

    try {
      setSaving(true);
      setError(null);

      const result = await gbpServicesApi.updateServices(publicId, updatedList);
      setServices(result);
      setDeleteTarget(null);
      setSuccess("Serviço excluído com sucesso!");
      setTimeout(() => setSuccess(null), 3000);

      // Se estava editando esse serviço, fechar o form
      if (editingIndex === index) resetForm();
    } catch {
      setError("Erro ao excluir serviço.");
      setTimeout(() => setError(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = () => {
    if (formMode === "add") handleAddService();
    else if (formMode === "edit") handleEditService();
  };

  // ---------- Renders ----------

  if (loading && !services.length) {
    return (
      <LoadingOverlay>
        <div className="spinner" />
        <p>Carregando serviços...</p>
      </LoadingOverlay>
    );
  }

  if (!publicId) {
    return (
      <Container>
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>
            {error || "Perfil não vinculado ao Google Business Profile."}
          </span>
        </Toast>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <HeaderRow>
        <SectionTitle>
          <span className="material-icons">home_repair_service</span>
          <FieldSyncBadge kind="backend" />
          Serviços
          <ServiceCount>({services.length})</ServiceCount>
        </SectionTitle>

        <AddServiceButton
          type="button"
          onClick={() => (formMode !== "closed" ? resetForm() : openAddForm())}
        >
          <span className="material-icons">
            {formMode !== "closed" ? "close" : "add"}
          </span>
          {formMode !== "closed" ? "Fechar" : "Novo Serviço"}
        </AddServiceButton>
      </HeaderRow>

      {/* Feedback */}
      {error && (
        <Toast $type="error">
          <span className="material-icons">error_outline</span>
          <span>{error}</span>
        </Toast>
      )}
      {success && (
        <Toast $type="success">
          <span className="material-icons">check_circle</span>
          <span>{success}</span>
        </Toast>
      )}

      {suggestedServices.length > 0 && (
        <SuggestionPanel>
          <SuggestionTitle>
            Sugestões de serviços com intenção de compra
          </SuggestionTitle>
          <SuggestionGrid>
            {suggestedServices.map((service) => (
              <SuggestionButton
                key={service.label}
                type="button"
                onClick={() => openSuggestedService(service)}
              >
                {service.label}
              </SuggestionButton>
            ))}
          </SuggestionGrid>
        </SuggestionPanel>
      )}

      {/* ---------- Formulário de Adição (topo) ---------- */}
      {formMode === "add" && (
        <FormOverlay>
          <ServiceForm>
            <FormTitle>
              <span className="material-icons">add_circle_outline</span>
              Novo Serviço
            </FormTitle>

            <FormGrid>
              <FormGroup>
                <Label>
                  Nome do Serviço *
                  <FieldSyncBadge kind="backend" />
                </Label>
                <Input
                  type="text"
                  value={formData.label || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, label: e.target.value })
                  }
                  placeholder="Ex: Corte de Cabelo"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Categoria
                  <FieldSyncBadge kind="backend" />
                </Label>
                <Select
                  value={formData.categoryId || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {formatCategoryLabel(cat)}
                    </option>
                  ))}
                  <option value="custom_service">Serviço Personalizado</option>
                </Select>
              </FormGroup>

              <FormGroup className="full-width">
                <Label>
                  Descrição
                  <FieldSyncBadge kind="backend" />
                </Label>
                <TextArea
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Descreva brevemente o serviço..."
                />
              </FormGroup>
            </FormGrid>

            <FormActions>
              <Btn type="button" $variant="ghost" onClick={resetForm}>
                <span className="material-icons">close</span>
                Cancelar
              </Btn>
              <Btn
                type="button"
                $variant="primary"
                onClick={handleFormSubmit}
                disabled={saving || !formData.label}
              >
                <span className="material-icons">add</span>
                {saving ? "Salvando..." : "Adicionar"}
              </Btn>
            </FormActions>
          </ServiceForm>
        </FormOverlay>
      )}

      {/* Filtro por categoria */}
      {uniqueCategories.length > 1 && (
        <FilterBar>
          <FilterChip
            type="button"
            $active={activeFilter === "all"}
            onClick={() => setActiveFilter("all")}
          >
            Todos ({services.length})
          </FilterChip>
          {uniqueCategories.map((cat) => (
            <FilterChip
              type="button"
              key={cat}
              $active={activeFilter === cat}
              onClick={() => setActiveFilter(cat)}
            >
              {formatCategoryLabel(cat)}
            </FilterChip>
          ))}
        </FilterBar>
      )}

      {/* Lista */}
      {filteredServices.length === 0 ? (
        <EmptyState>
          <span className="material-icons">handyman</span>
          <h4>Nenhum serviço encontrado</h4>
          <p>
            {services.length === 0
              ? "Adicione os serviços que sua empresa oferece."
              : "Nenhum serviço nessa categoria."}
          </p>
        </EmptyState>
      ) : (
        <ServicesList>
          {filteredServices.map((service) => {
            // Find real index in the full services array
            const realIndex = services.indexOf(service);
            const cat = service.categoryId || service.serviceTypeId;

            return (
              <ServiceCard key={`svc-${realIndex}`}>
                <ServiceIcon>
                  <span className="material-icons">
                    {service.serviceTypeId ? "verified" : "build_circle"}
                  </span>
                </ServiceIcon>

                <ServiceBody>
                        {/* Se estiver no modo de edição para esse item, renderiza o formulário inline */}
                        {formMode === "edit" && editingIndex === realIndex ? (
                          <ServiceForm style={{ marginTop: 0 }}>
                            <FormTitle>
                              <span className="material-icons">edit</span>
                              Editar Serviço
                            </FormTitle>

                            <FormGrid>
                              <FormGroup>
                                <Label>
                                  Nome do Serviço *
                                  <FieldSyncBadge kind="backend" />
                                </Label>
                                <Input
                                  type="text"
                                  value={formData.label || ""}
                                  onChange={(e) =>
                                    setFormData({ ...formData, label: e.target.value })
                                  }
                                  placeholder="Ex: Corte de Cabelo"
                                />
                              </FormGroup>

                              <FormGroup>
                                <Label>
                                  Categoria
                                  <FieldSyncBadge kind="backend" />
                                </Label>
                                <Select
                                  value={formData.categoryId || ""}
                                  onChange={(e) =>
                                    setFormData({ ...formData, categoryId: e.target.value })
                                  }
                                >
                                  <option value="">Selecione uma categoria</option>
                                  {categories.map((cat) => (
                                    <option key={cat} value={cat}>
                                      {formatCategoryLabel(cat)}
                                    </option>
                                  ))}
                                  <option value="custom_service">Serviço Personalizado</option>
                                </Select>
                              </FormGroup>

                              <FormGroup className="full-width">
                                <Label>
                                  Descrição
                                  <FieldSyncBadge kind="backend" />
                                </Label>
                                <TextArea
                                  value={formData.description || ""}
                                  onChange={(e) =>
                                    setFormData({ ...formData, description: e.target.value })
                                  }
                                  placeholder="Descreva brevemente o serviço..."
                                />
                              </FormGroup>
                            </FormGrid>

                            <FormActions>
                              <Btn type="button" $variant="ghost" onClick={resetForm}>
                                <span className="material-icons">close</span>
                                Cancelar
                              </Btn>
                              <Btn
                                type="button"
                                $variant="primary"
                                onClick={handleFormSubmit}
                                disabled={saving || !formData.label}
                              >
                                <span className="material-icons">save</span>
                                {saving ? "Salvando..." : "Salvar Alterações"}
                              </Btn>
                            </FormActions>
                          </ServiceForm>
                        ) : (
                          <>
                            <ServiceLabel>
                              {service.label ||
                                formatCategoryLabel(service.serviceTypeId) ||
                                "Serviço sem nome"}
                            </ServiceLabel>

                            <ServiceMeta>
                              {cat && (
                                <CategoryBadge>
                                  {formatCategoryLabel(cat)}
                                </CategoryBadge>
                              )}
                              {service.serviceTypeId ? (
                                <TypeBadge>Predefinido</TypeBadge>
                              ) : (
                                <TypeBadge>Personalizado</TypeBadge>
                              )}
                            </ServiceMeta>

                            {service.description && (
                              <ServiceDescription>
                                {service.description}
                              </ServiceDescription>
                            )}
                          </>
                        )}
                </ServiceBody>

                <ActionButtons>
                  <IconBtn
                    type="button"
                    title="Editar serviço"
                    $color="#4285f4"
                    $hoverBg="#eff6ff"
                    onClick={() => openEditForm(realIndex)}
                    disabled={saving}
                  >
                    <span className="material-icons">edit</span>
                  </IconBtn>
                  <IconBtn
                    type="button"
                    title="Excluir serviço"
                    $color="#ef4444"
                    $hoverBg="#fef2f2"
                    onClick={() =>
                      setDeleteTarget({
                        index: realIndex,
                        label:
                          service.label ||
                          formatCategoryLabel(service.serviceTypeId) ||
                          "Serviço sem nome",
                      })
                    }
                    disabled={saving}
                  >
                    <span className="material-icons">delete</span>
                  </IconBtn>
                </ActionButtons>
              </ServiceCard>
            );
          })}
        </ServicesList>
      )}

      {/* ---------- Dialog de Confirmação de Exclusão ---------- */}
      {deleteTarget && (
        <ConfirmOverlay onClick={() => !saving && setDeleteTarget(null)}>
          <ConfirmBox onClick={(e) => e.stopPropagation()}>
            <ConfirmTitle>
              <span className="material-icons">warning</span>
              Excluir Serviço
            </ConfirmTitle>
            <ConfirmText>
              Tem certeza que deseja excluir o serviço{" "}
              <ConfirmServiceName>"{deleteTarget.label}"</ConfirmServiceName>?
              <br />
              Esta ação não pode ser desfeita.
            </ConfirmText>
            <ConfirmActions>
              <Btn
                type="button"
                $variant="ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={saving}
              >
                Cancelar
              </Btn>
              <Btn
                type="button"
                $variant="danger"
                onClick={() => handleDeleteService(deleteTarget.index)}
                disabled={saving}
              >
                <span className="material-icons">delete</span>
                {saving ? "Excluindo..." : "Excluir"}
              </Btn>
            </ConfirmActions>
          </ConfirmBox>
        </ConfirmOverlay>
      )}
    </Container>
  );
};

export default ServicesTab;
